import { useEffect, useState } from "react";

/**
 * Die EINFALTUNG der Kopffelder in den Vorgangs-Formularen (Paket „Positionen mit Präsenz",
 * Entscheid Dirk 08.09.2026). Seit dem Abschnittsmuster der Vorgangsdialoge (Spec „Dateien am
 * Vorgang“) zeichnet sie der Baustein `Vorgangsabschnitt`; hier bleibt der Zustandsgriff
 * `useKopffaltung` (je Abschnitt gebündelt in `vorgangsabschnitt/sectionFolds.ts`).
 *
 * **Warum es diesen Baustein gibt.** Im Bearbeiten-Panel von Angebot und Auftrag wird die
 * meiste Arbeit an den POSITIONEN geleistet — und genau die standen bis hierher ganz unten,
 * hinter elf Kopffeldern, Notiz, Einleitungs- und Ausleitungstext. Wer eine Menge ändern
 * wollte, scrollte an einem Formular vorbei, das er gar nicht anfassen wollte. Die
 * Kopffelder werden deshalb zu EINEM aufklappbaren Abschnitt, der beim Bearbeiten
 * zugeklappt beginnt; die Positionen rücken darüber hinauf.
 *
 * **Warum ein Baustein und nicht zweimal derselbe Handgriff.** Angebot und Auftrag tragen
 * dieselbe Faltung, und die Rechnung trägt sie genauso. Zweimal abgeschrieben
 * wären es beim nächsten Fix zwei verschiedene Faltungen — eine mit Auto-Aufklappen bei
 * Feldfehler, eine ohne, und die ohne verstecke dann den Fehler, um dessentwillen es die
 * Mechanik gibt.
 *
 * **Der Zustand gehört der MASKE, nicht dem Baustein.** `useKopffaltung` gibt ihn als
 * Griff heraus, statt ihn im Kopfblock zu verstecken. Zwei Gründe, und beide sind teuer
 * gelernt:
 *
 *  - Der Startzustand hängt vom WEG ab (Bearbeiten zu, Anlegen auf), und zurückgesetzt wird
 *    er dort, wo auch der Entwurf zurückgesetzt wird — beim Neu-Öffnen, beim Wechsel des
 *    bearbeiteten Vorgangs, beim Kunden- oder Bereichswechsel. Kein Zustand darf einen
 *    Kontextwechsel überleben; die Stellen, die das wissen, sind die Masken.
 *  - Das Auto-Aufklappen beim Speichern-Versuch (`zeigeFeld`) stösst die PRÜFUNG an, und die
 *    steht in der Maske. Ein Baustein, der seinen Zustand für sich behielte, bräuchte dafür
 *    einen imperativen Griff von aussen — also denselben Griff, nur umständlicher.
 *
 * **Eingeklappt heisst versteckt, nicht abgeräumt** (`hidden` statt Ausbau aus dem Baum).
 * `aria-controls` zeigt auf den Inhalt; zeigte es auf etwas, das es zeitweise gar nicht
 * gibt, wäre die Beziehung die Hälfte der Zeit gebrochen. Nebenbei bleiben die Felder
 * dieselben Knoten, sodass ein Auf- und Zuklappen weder Fokus noch Eingabe kostet.
 */

export type Kopffaltung = {
  /** Steht der Kopfblock offen? */
  offen: boolean;
  /** Auf- bzw. zuklappen (die Kopfzeile ruft es). */
  umschalten: () => void;
  /**
   * Aufklappen und danach das Feld mit dieser Kennung fokussieren — der Weg für einen
   * Pflichtfeld-Befund beim Speichern-Versuch.
   *
   * Die REIHENFOLGE ist der ganze Sinn: In einem `hidden`-Abschnitt lässt sich nichts
   * fokussieren. Deshalb wird der Wunsch gemerkt und erst in einem Effekt eingelöst, wenn
   * der Abschnitt gezeichnet und offen ist. Ein `focus()` gleich im Klick liefe gegen den
   * Stand VOR dem Aufklappen und ginge lautlos ins Leere — ein unsichtbarer Fehler, also
   * genau das, wogegen die Mechanik gebaut ist.
   */
  zeigeFeld: (feldId: string) => void;
  /** Auf den Startzustand zurück (Neu-Öffnen, Kontextwechsel). */
  zuruecksetzen: () => void;
  /**
   * Nur aufklappen, ohne Fokuswunsch — für Feldfehler des Servers (ME-Etappe 3, Aufgabe 4):
   * Den Fokus setzt dort `me-formular.zumErstenFehler()` nach dem Zeichnen, und der findet
   * das Feld nur, wenn der Block im SELBEN Zug offen ist (in `hidden` lässt sich nichts
   * fokussieren).
   */
  oeffnen: () => void;
};

export function useKopffaltung(startOffen: boolean): Kopffaltung {
  const [offen, setOffen] = useState(startOffen);
  /** Das Feld, das nach dem nächsten Zeichnen den Fokus bekommen soll (null = keines). */
  const [fokusFeld, setFokusFeld] = useState<string | null>(null);

  useEffect(() => {
    if (fokusFeld === null) return;
    // Erst den Wunsch löschen, dann einlösen: Sonst liefe derselbe Fokus bei jedem
    // weiteren Zeichnen erneut und risse den Bearbeiter aus dem Feld, in das er inzwischen
    // gewechselt ist.
    setFokusFeld(null);
    const feld = document.getElementById(fokusFeld);
    // `focus` steht hinter einer Prüfung, wie `scrollIntoView` in der Bereichsfaltung: Ein
    // Aufruf ins Leere liesse jeden Test scheitern, der ein Formular abschickt.
    if (feld !== null && typeof feld.focus === "function") feld.focus();
  }, [fokusFeld]);

  return {
    offen,
    umschalten: () => setOffen((v) => !v),
    zeigeFeld: (feldId: string) => { setOffen(true); setFokusFeld(feldId); },
    zuruecksetzen: () => { setOffen(startOffen); setFokusFeld(null); },
    oeffnen: () => setOffen(true),
  };
}
